import config from './config';

export interface BlogItem {
  id: number;
  infoName: string;
  infoHeader: string;
  inforDetails: string;
  infoType: string;
  tags: string;
  mainimage: string | null;
  createdDate: string;
  inforPagenav?: string | null;
  pageTitle?: string | null;
}

type SiteContentResponse = {
  id: number;
  contentType: string;
  slug: string;
  title: string;
  summary?: string | null;
  htmlContent: string;
  tags?: string | null;
  mainImageUrl?: string | null;
  mainImageAlt?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  seoTitle?: string | null;
};

const mapSiteContentToBlog = (item: SiteContentResponse): BlogItem => ({
  id: item.id,
  infoName: item.title,
  infoHeader: item.summary ?? '',
  inforDetails: item.htmlContent,
  infoType: item.contentType,
  tags: item.tags ?? '',
  mainimage: item.mainImageUrl ?? null,
  createdDate: item.publishedAt || item.createdAt,
  inforPagenav: item.slug,
  pageTitle: item.seoTitle ?? null,
});

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const getBlogSlug = (blog: BlogItem): string => {
  const explicit = (blog.inforPagenav ?? '').trim();
  if (explicit) return slugify(explicit);
  return slugify(blog.infoName) || String(blog.id);
};

export const fetchBlogs = async (): Promise<BlogItem[]> => {
  const response = await fetch(`${config.apiBaseUrl}/api/sitecontent?contentType=blogs`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    mode: 'cors',
  });

  if (!response.ok) {
    throw new Error(`Blog API responded with ${response.status}`);
  }

  const raw: unknown = await response.json();
  const arr = Array.isArray(raw) ? raw as SiteContentResponse[] : [];

  return arr
    .map(mapSiteContentToBlog)
    .filter((item) => item && typeof item.infoName === 'string' && item.infoName.trim() !== '');
};

export const fetchBlogByNav = async (pagenav: string): Promise<BlogItem | null> => {
  const safeNav = pagenav.trim();
  if (!safeNav) return null;

  const response = await fetch(`${config.apiBaseUrl}/api/sitecontent/by-nav/${encodeURIComponent(safeNav)}?contentType=blogs`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    mode: 'cors',
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Blog detail API responded with ${response.status}`);
  }

  const raw: unknown = await response.json();
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as SiteContentResponse;
  if (!item.title || typeof item.title !== 'string') {
    return null;
  }

  return mapSiteContentToBlog(item);
};
